import { useEffect, useMemo, useRef, useState } from "react"

import { App, Divider, Flex, Space, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../Components/Button"
import ExportDropdown from "../../../../Components/Export"
import ListViewToggle from "../../../../Components/ListViewToggle/ListViewToggle"
import ManageColumn from "../../../../Components/ManageColumns"
import RecoverConfigureBanner from "../../../../Components/RecoverConfigureBanner"
import { useTitleSectionAction } from "../../../../Components/TitleSection/TitleSectionContext"
import { RoutesPaths } from "../../../../constants/user-routes"
import { useDisputesQuery } from "../../hooks/useDisputesQuery"
import {
  resetFilters,
  setDisputeColumns,
  setDisputeViewMode,
  setDisputeVisibleFilters,
  setFilters
} from "../../redux/disputes.slice"
import {
  assignDispute,
  getDisputes,
  getDisputesExport,
  getReasonCategories,
  getTeamMembers,
  pinDispute,
  syncDisputes
} from "../../redux/disputes.thunks"
import { getProfile } from "../ConfigurePage/redux/configure.thunks"
import AssigneeCell from "./components/AssigneeCell/AssigneeCell"
import DisputesFilterBar from "./components/DisputesFilterBar/DisputesFilterBar"
import DisputesHeaderActions from "./components/DisputesHeaderActions/DisputesHeaderActions"
import DisputesList from "./components/DisputesList/DisputesList"
import DisputesMetricCards from "./components/DisputesMetricCards/DisputesMetricCards"
import DisputesPagination from "./components/DisputesPagination/DisputesPagination"
import { buildColumns, buildFieldDefs } from "./components/DisputesTable/columns"
import DisputesTable from "./components/DisputesTable/DisputesTable"
import DisputesTableEmpty from "./components/DisputesTable/TableEmpty"
import { isFiltersDirty, transactionUrl } from "./constants/allPageData"
import { DEFAULT_VISIBLE_FILTERS } from "./constants/columns"
import { REASON_CATEGORY_FALLBACK } from "./constants/reasonCategories"
import { STATUS_LABELS, STATUS_TAG_COLORS } from "./constants/statusMeta"

import "../../styles.less"

const { Text } = Typography

const DisputesAllPage = ({
  results,
  count,
  statistics,
  fetching,
  exporting,
  syncing,
  filters,
  tableColumns,
  viewMode,
  isMobile,
  pinTick,
  reasonCategoryOptions,
  profileLoaded,
  fetchDisputes,
  exportDisputes,
  syncDisputes,
  fetchProfile,
  setFilters,
  resetFilters,
  getReasonCategories,
  pinDispute,
  setColumns,
  setViewMode,
  visibleFilters,
  setVisibleFilters,
  theme,
  isShowDemoData,
  buildingBlocks,
  teamMembers,
  assignDispute,
  getTeamMembers
}) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const navigate = useNavigate()
  const { setAction } = useTitleSectionAction()
  // The table needs horizontal room this viewport doesn't have, so mobile always
  // renders cards no matter what the stored viewMode says.
  const view = isMobile ? "cards" : viewMode
  // Page-header action slot, the same portal target Deflect and Team use.
  // Page, ordering and the refetch effect live in hooks/useDisputesQuery.
  const { page, setPage, ordering, pagedResults, handleChangeTable } = useDisputesQuery({
    filters,
    results,
    pinTick,
    isShowDemoData,
    fetchDisputes
  })
  // Which filters are shown as chips lives in redux (state.disputes.table) and is
  // persisted to localStorage + the backend profile, so the choice survives a
  // reload (same plumbing as Edit-columns).
  const [addFilterOpen, setAddFilterOpen] = useState(false)
  const [filterSearch, setFilterSearch] = useState("")

  // "Last synced" indicator: stamp the time whenever a fetch completes (the list
  // is the freshly-pulled, synced data). Lets us show when the data was last
  // refreshed next to the Sync button, alongside the auto-sync-frequency tooltip.
  const [lastSynced, setLastSynced] = useState(null)
  const wasFetching = useRef(false)
  useEffect(() => {
    if (wasFetching.current && !fetching) setLastSynced(new Date())
    wasFetching.current = fetching
  }, [fetching])

  // Mobile Sync/Export live in the page header's action slot. Registered through
  // TitleSectionContext (the ReceiptPage pattern) rather than portalled into
  // #page-header-actions: on mobile TitleSection renders that node only when it is
  // given children, which the Layout never does, so the portal had nowhere to land.
  useEffect(() => {
    if (!isMobile) return undefined
    setAction(
      <DisputesHeaderActions
        syncing={syncing}
        exporting={exporting}
        count={count}
        onSync={handleSync}
        onExport={handleExport}
      />
    )
    return () => setAction(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, syncing, exporting, count])

  // Invited team members populate the Assignee column's assign menu (fetched via
  // the disputes slice so a failure stays quiet, see getTeamMembers).
  const members = useMemo(
    () => (Array.isArray(teamMembers) ? teamMembers : teamMembers?.results || []),
    [teamMembers]
  )
  useEffect(() => {
    if (!members.length) getTeamMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    document.title = "Disputes - Chargeback"
  }, [])

  // Pull the profile (once) if it isn't loaded yet: it carries the cross-device
  // table prefs, which the reducer hydrates over the localStorage fallback.
  useEffect(() => {
    if (!profileLoaded) fetchProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!reasonCategoryOptions || reasonCategoryOptions.length === 0) getReasonCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const categoryOptions = useMemo(
    () =>
      reasonCategoryOptions && reasonCategoryOptions.length
        ? reasonCategoryOptions
        : REASON_CATEGORY_FALLBACK,
    [reasonCategoryOptions]
  )

  const labelForCategory = useMemo(() => {
    const map = new Map(categoryOptions.map((o) => [o.value, o.label]))
    return (key) =>
      map.get(key) || (key ? key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "")
  }, [categoryOptions])

  // The card view's page slice. The table gets this for free (antd Table slices its
  // dataSource from the same page/pageSize), but the card grid was handed the whole
  // result set, so it rendered every row while its own footer said "Showing 1-50 of
  // 200": wrong on screen, and hundreds of cards' worth of DOM and antd instances.

  // The filter strip owns its own registry, chips and overflow scrolling; the page
  // keeps only the `filtersDirty` read, which also gates the metric cards, and the
  // reset itself, which the empty state offers as well.
  const filtersDirty = isFiltersDirty(filters)

  const handleResetFilters = () => {
    resetFilters()
    setVisibleFilters([...DEFAULT_VISIBLE_FILTERS])
    setPage(1)
  }

  const handlePin = (record) => pinDispute({ id: record.id, pin: !record.pinned })

  // Export all rows, or just the current filtered set ('Export as filtered'). The thunk
  // hands the blob to the browser; what the merchant is told about it belongs here, where
  // the click happened — same for the two handlers below.
  const handleExport = async (type) => {
    try {
      await exportDisputes({ as_filtered: type !== "all" }).unwrap()
      notification.success({ message: t("Your export has downloaded.") })
    } catch {
      /* error handled in thunk */
    }
  }

  const handleSync = async () => {
    try {
      const { synced } = await syncDisputes().unwrap()
      notification.success({
        message:
          synced > 0
            ? t("Synced _n_ disputes from your processor.", { n: synced })
            : t("You're up to date. No new disputes.")
      })
    } catch {
      /* error handled in thunk */
    }
  }

  // `emailed` is false while the assign endpoint is still unwired on this backend — the
  // row keeps its optimistic assignee, we just don't promise an email that never went out.
  const handleAssign = async (payload) => {
    try {
      const { emailed } = await assignDispute(payload).unwrap()
      if (!payload.member) {
        notification.info({ message: t("Assignment cleared.") })
        return
      }
      notification.success({
        message: emailed
          ? t("Assigned to _name_. We emailed them the dispute.", { name: payload.member.name })
          : t("Assigned to _name_.", { name: payload.member.name })
      })
    } catch {
      /* error handled in thunk */
    }
  }

  // Column definitions live in components/DisputesTable/columns.jsx; the page keeps
  // only the memoisation, with the dependency lists these two had inline.
  const fieldDefs = useMemo(
    () => buildFieldDefs({ t, members, labelForCategory, onAssign: handleAssign }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, labelForCategory, ordering, members]
  )

  const columns = useMemo(
    () => buildColumns({ t, tableColumns, fieldDefs, onPin: handlePin }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableColumns, fieldDefs, t]
  )

  const openDetails = (record) => navigate(RoutesPaths.recover.details(record.id))

  // Empty state: distinguish "filters/search returned nothing" (offer a reset)
  // from "this account genuinely has no disputes yet". Mirrors how Alerts
  // branches on the total count. statistics.all is the unfiltered total.
  // Activated but no processor connected yet (and no disputes): point them to Configure
  // Step 1 instead of the generic "no disputes" copy that assumes a connection.
  const noProcessor = (buildingBlocks?.processors?.connected || []).length === 0
  // A genuinely empty account (no disputes at all, nothing narrowed) has nothing to
  // count or filter, so the metric cards (six zeroes) and the filter strip are hidden
  // and the empty state speaks for itself. They come back the moment the first dispute
  // lands, or if the merchant has narrowed the list themselves.

  // todo I don't know what this is.
  // const listIsEmptyAccount = !filtersDirty && profileLoaded && (statistics?.all ?? 0) === 0
  const listIsEmptyAccount = false

  const TableEmpty = () => (
    <DisputesTableEmpty
      filtersDirty={filtersDirty}
      statistics={statistics}
      profileLoaded={profileLoaded}
      noProcessor={noProcessor}
      onResetFilters={handleResetFilters}
    />
  )

  return (
    <div className="disputes-all-page">
      <RecoverConfigureBanner />
      {listIsEmptyAccount ? null : (
        <DisputesMetricCards
          statistics={statistics}
          count={count}
          filters={filters}
          filtersDirty={filtersDirty}
          setFilters={setFilters}
          setPage={setPage}
          theme={theme}
        />
      )}

      {listIsEmptyAccount ? null : <Divider style={{ margin: 0 }} />}

      {listIsEmptyAccount ? null : (
        <Flex gap={8} align="center" className="disputes-filters-row">
          {/* Scrollable single-row filter strip + overflow arrow (Alerts pattern). */}
          <DisputesFilterBar
            filters={filters}
            setFilters={setFilters}
            visibleFilters={visibleFilters}
            setVisibleFilters={setVisibleFilters}
            onResetFilters={handleResetFilters}
            setPage={setPage}
            members={members}
            addFilterOpen={addFilterOpen}
            setAddFilterOpen={setAddFilterOpen}
            filterSearch={filterSearch}
            setFilterSearch={setFilterSearch}
          />

          {/* Sync and Export move OUT of the filter row on mobile and into the page
            header's action slot as icon-only buttons (see the portal above), matching
            the Alerts page. At 375px the filter row has no space for two labelled
            buttons beside the filter chips. */}
          <Space style={{ marginLeft: "auto" }} size={8}>
            {isMobile ? null : (
              <>
                <Flex align="center" gap={8} className="disputes-sync">
                  {lastSynced ? (
                    <Text type="secondary" className="disputes-sync_meta">
                      {t("Synced _time_", { time: dayjs(lastSynced).format("HH:mm") })}
                    </Text>
                  ) : null}
                  <Tooltip
                    title={t("Disputes sync automatically every hour. Click to refresh now.")}
                  >
                    <Button
                      className="center"
                      loading={syncing}
                      onClick={handleSync}
                      text={t("Sync disputes")}
                    />
                  </Tooltip>
                </Flex>
                <ExportDropdown
                  loading={exporting}
                  onExport={handleExport}
                  exportAsFiltered
                  disabled={!count}
                />
              </>
            )}
            {view === "table" ? (
              <ManageColumn
                myColumns={tableColumns}
                onChangeColumns={setColumns}
                iconsType="disputes"
                pinnedColumns={["Status"]}
              />
            ) : null}
            {/* Mobile is cards-only, so the switcher (and the column manager it pairs
              with) has nothing to offer there. The stored viewMode is left alone, so
              a desktop user's table preference survives a trip through a phone. */}
            {isMobile ? null : <ListViewToggle view={view} setView={setViewMode} />}
          </Space>
        </Flex>
      )}

      {view === "table" ? (
        <DisputesTable
          loading={fetching}
          results={results}
          columns={columns}
          count={count}
          page={page}
          onChange={handleChangeTable}
          onRowClick={openDetails}
          EmptyComponent={TableEmpty}
        />
      ) : (
        <Flex vertical gap={16}>
          <DisputesList
            results={pagedResults}
            fetching={fetching}
            EmptyState={TableEmpty}
            statusColors={STATUS_TAG_COLORS}
            statusLabels={STATUS_LABELS}
            labelForCategory={labelForCategory}
            getTransactionUrl={transactionUrl}
            onView={openDetails}
            onPin={handlePin}
            /* Same control as the table's Assignee column, handed to the card rather
               than rebuilt there, so assigning behaves identically in both views. */
            renderAssignee={(row) => (
              <AssigneeCell
                row={row}
                members={members}
                onAssign={(member) =>
                  handleAssign({ id: row.id, member, prev: row.assigned_to || null })
                }
                t={t}
              />
            )}
          />
          {Array.isArray(results) && results.length > 0 ? (
            <DisputesPagination count={count} page={page} onChange={setPage} />
          ) : null}
        </Flex>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  results: state.disputes.list.results,
  count: state.disputes.list.count,
  statistics: state.disputes.list.statistics,
  fetching: state.disputes.list.fetching,
  exporting: state.disputes.list.exporting,
  syncing: state.disputes.list.syncing,
  filters: state.disputes.filters,
  tableColumns: state.disputes.table.columns,
  viewMode: state.disputes.table.viewMode,
  visibleFilters: state.disputes.table.visibleFilters,
  pinTick: state.disputes.table.pinTick,
  reasonCategoryOptions: state.disputes.reasonCategories?.options ?? [],
  profileLoaded: state.disputesConfigure.profile.profileLoaded,
  buildingBlocks: state.disputesConfigure.profile.buildingBlocks,
  theme: state.nav.theme,
  isMobile: state.nav.isMobile,
  // Invited team members for the Assignee column (disputes-slice fetch, graceful).
  teamMembers: state.disputes.team.members,
  // Refetch the list when "show demo data" flips, matching Alerts/Credits/Health.
  isShowDemoData: state.auth?.userInfo?.is_show_demo_data
})

const mapDispatchToProps = (dispatch) => ({
  fetchDisputes: (filters) => dispatch(getDisputes({ filters })),
  exportDisputes: (payload) => dispatch(getDisputesExport(payload)),
  syncDisputes: () => dispatch(syncDisputes()),
  assignDispute: (payload) => dispatch(assignDispute(payload)),
  getTeamMembers: () => dispatch(getTeamMembers()),
  fetchProfile: () => dispatch(getProfile()),
  setFilters: (patch) => dispatch(setFilters(patch)),
  resetFilters: () => dispatch(resetFilters()),
  getReasonCategories: () => dispatch(getReasonCategories()),
  pinDispute: (payload) => dispatch(pinDispute(payload)),
  setColumns: (cols) => dispatch(setDisputeColumns(cols)),
  setViewMode: (mode) => dispatch(setDisputeViewMode(mode)),
  setVisibleFilters: (keys) => dispatch(setDisputeVisibleFilters(keys))
})

export default connect(mapStateToProps, mapDispatchToProps)(DisputesAllPage)
